import { get } from 'svelte/store';
import { home_data } from 'src/stores/account/user';
import { get_mode, is_gen_2, isProThreeEM, real_id } from 'src/helpers/interface_helpers';
import {
	getPVSetupDevices,
	type PVSetupTypes,
} from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
import { getPvSetupStatuses } from 'src/components/Consumption/PVSetupEnergy/pv_setup_live_calculate_helper';
import type { Device } from 'src/types/Device';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';

export type PvGenerationFlow = {
	hasPvSetup: true;
	gridImport: number;
	solarPower: number;
	solarToHome: number;
	exportPower: number;
	homeConsumption: number;
};

export const getPvDevicePower = (
	dev: Device,
	status: Record<string, unknown>,
	settings: Record<string, unknown>,
): number => {
	if (!status) return 0;

	if (!is_gen_2(dev)) {
		if (dev.type == 'SHEM-3') {
			return ((status?.emeters as { power: number }[]) || [0])
				.map((i) => i.power)
				.reduce((a, b) => a + b, 0);
		}
		return (status?.emeters as { power: number }[])?.[dev.channel]?.power || 0;
	}

	if (isProThreeEM(dev.type) && get_mode(dev, status, settings) !== 'monophase') {
		return (status?.['em:0'] as { total_act_power?: number })?.total_act_power || 0;
	}
	if (status?.[`em1:${dev.channel}`]) {
		return (status[`em1:${dev.channel}`] as { act_power?: number })?.act_power || 0;
	}
	if (status?.[`switch:${dev.channel}`]) {
		return (status[`switch:${dev.channel}`] as { apower?: number })?.apower || 0;
	}
	if (status?.[`pm1:${dev.channel}`]) {
		return (status[`pm1:${dev.channel}`] as { apower?: number })?.apower || 0;
	}
	return 0;
};

export const getActivePvSetupType = (): PVSetupTypes => {
	return get(home_data)?.net_metering_type || 'Parallel';
};

/** Live generation flow aligned with Home Settings PV set-up energy distribution. */
export const getPvGenerationFlow = (
	pvSetupType: PVSetupTypes,
	statuses: Record<string, Record<string, unknown>>,
	settings: Record<string, Record<string, unknown>>,
): PvGenerationFlow | null => {
	const { gridDevices, solarDevices } = getPVSetupDevices(pvSetupType);
	if (!gridDevices.length || !solarDevices.length) return null;

	let gridPower = 0;
	let generatedPower = 0;

	gridDevices.forEach((dev) => {
		gridPower += getPvDevicePower(dev, statuses[real_id(dev.id)], settings[real_id(dev.id)]) || 0;
	});
	solarDevices.forEach((dev) => {
		generatedPower +=
			getPvDevicePower(dev, statuses[real_id(dev.id)], settings[real_id(dev.id)]) || 0;
	});

	const pvData = getPvSetupStatuses(gridPower, generatedPower, pvSetupType, false);
	const gridImport = Math.max(0, pvData.gridPower);
	const exportPower = pvData.solarToGrid
		? Math.max(0, pvData.solarPower + Math.max(0, pvData.gridPower) - pvData.houseConsumption)
		: 0;
	const solarToHome = pvData.solarToHome ? Math.max(0, pvData.houseConsumption - gridImport) : 0;

	return {
		hasPvSetup: true,
		gridImport,
		solarPower: Math.abs(pvData.solarPower),
		solarToHome,
		exportPower,
		homeConsumption: pvData.houseConsumption,
	};
};

export const getCurrentPvGenerationFlow = (): PvGenerationFlow | null =>
	getPvGenerationFlow(
		getActivePvSetupType(),
		get(devices_status) as Record<string, Record<string, unknown>>,
		get(devices_settings) as Record<string, Record<string, unknown>>,
	);

export const hasMonitoringDevices = (devices: Record<string, Device>): boolean =>
	Object.values(devices).some((device) => device && !device.shared);
